import { ModelToRun } from '@/scenes/engine/common/constants'
import {
  generateImageBaseOnImage,
  type GenerateImageAnalyticsOptions,
  type GenerateImageSafetyOptions,
} from './imageService'

export interface EditWithAiRequest {
  /** Source images as data URLs. Single-element for AI Studio and Batch Studio. */
  images: string[]
  prompt: string
  /** Target frame aspect ratio; omit to keep the model's native ratio (AI Studio, Batch). */
  aspectRatio?: number
  /** Template-declared generation quality. Omit to let the provider pick its default. */
  quality?: number
  model?: ModelToRun
  analytics?: GenerateImageAnalyticsOptions
  safety?: GenerateImageSafetyOptions
  /** Caller-owned. The runner never creates or aborts a controller. */
  signal: AbortSignal
}

export type EditWithAiOutcome =
  | { status: 'success'; image: string }
  | { status: 'aborted' }
  | { status: 'failed'; error?: unknown }

function isAbortError(error: unknown): boolean {
  return (error as { code?: string } | null)?.code === 'ERR_CANCELED'
}

/**
 * One AI image edit: source images + prompt -> new image bytes.
 *
 * Deliberately does NOT own: the abort controller, loading/lock state, resizing,
 * persistence, or any analytics beyond the 'Edit with AI' event that
 * `generateImageBaseOnImage` already fires. Each consumer's model for those is
 * different (fabric+media-repo, AI Studio session records, batch redux slice), so
 * they stay in the adapters. What this centralizes is the aborted-vs-failed-vs-
 * success discrimination, which is where the three copies currently disagree —
 * `generateImageBaseOnImage` both rejects (abort/network errors bubbling from the
 * gemini/qwen/flux calls) and resolves `{success:false}` (NSFW block, empty
 * result), and only a signal check tells the two failure shapes apart from a
 * genuine user cancel.
 */
export async function runEditWithAi(req: EditWithAiRequest): Promise<EditWithAiOutcome> {
  const { images, prompt, aspectRatio, quality, model, analytics, safety, signal } = req

  try {
    const result = await generateImageBaseOnImage(
      images,
      prompt,
      model ?? ModelToRun.DEFAULT,
      null,
      aspectRatio,
      quality ?? null,
      signal,
      analytics,
      undefined,
      safety,
    )

    if (signal.aborted) return { status: 'aborted' }
    if (!result.success || !result.image) return { status: 'failed' }
    return { status: 'success', image: result.image }
  } catch (error) {
    if (signal.aborted || isAbortError(error)) return { status: 'aborted' }
    return { status: 'failed', error }
  }
}
